import React from 'react';
import { InfoOutlineIcon } from '@chakra-ui/icons';
import {
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverTrigger,
  Skeleton,
  Text,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { AlertDialogModal } from 'clipsal-cortex-ui/src/components/AlertDialogModal';

import {
  CreateSiteConsent,
  useCreateSiteConsentMutation,
  useGetSiteConsentByTypeQuery,
  useUpdateSiteConsentMutation,
} from '../../../site/new-user-journey/consentApi';
import { selectSite } from '../../../site/siteSlice';
import { selectUser } from '../../../user/userSlice';
import { SettingSwitch } from '../SettingSwitch';

export function CustomerSupportConsentToggle() {
  const { site_id: siteId } = useSelector(selectSite);
  const user = useSelector(selectUser);
  const { isOpen: isAlertOpen, onOpen: onAlertOpen, onClose: onCloseAlert } = useDisclosure();
  const [updateSiteConsent] = useUpdateSiteConsentMutation();
  const [createSiteConsent] = useCreateSiteConsentMutation();
  const { data: shareDataWithCustomerSupportConsent, isLoading } =
    useGetSiteConsentByTypeQuery('SHARE_WITH_CUSTOMER_CARE');
  const toast = useToast({ isClosable: true });
  const isLoaded = !isLoading;
  const bgColor = useColorModeValue('#fff', 'customGrey.700');
  const { t } = useTranslation();

  const handleCustomerSupportConsentToggle = async (hasPermission: boolean) => {
    if (!hasPermission && !isAlertOpen) {
      return onAlertOpen();
    }
    try {
      if (shareDataWithCustomerSupportConsent) {
        await updateSiteConsent({
          siteId,
          consentId: shareDataWithCustomerSupportConsent.id,
          body: { permission: hasPermission },
        }).unwrap();
      } else {
        const body: CreateSiteConsent = {
          permission: hasPermission,
        };
        await createSiteConsent({ siteId, body }).unwrap();
      }
    } catch (error) {
      console.error(error);
      toast({
        title: t('Customer Support Consent.error saving'),
        description: `${t('Common.please try again')} ${t('Common.if this persists contact support')}`,
        status: 'error',
      });
    }
  };

  return (
    <>
      <Skeleton isLoaded={isLoaded} borderRadius="5px" borderBottomRightRadius="25px">
        <SettingSwitch
          isDisabled={user.role !== 'SYSTEM_OWNER'}
          onChange={handleCustomerSupportConsentToggle}
          isChecked={shareDataWithCustomerSupportConsent?.permission || false}
          data-testid="share-my-data-with-customer-support-switch"
          alignItems="center"
          text={t('Customer Support Consent.share my energy data')}
        >
          <Popover>
            <PopoverTrigger>
              <IconButton
                size={'lg'}
                aria-label={'More info'}
                variant={'ghost'}
                icon={<InfoOutlineIcon />}
                data-testid="customer-support-consent-info-popover-button"
              />
            </PopoverTrigger>
            <PopoverContent
              borderRadius={8}
              w="200px"
              border="none"
              bg={bgColor}
              data-testid="customer-support-consent-info-popover-content"
            >
              <PopoverArrow bg={bgColor} />
              <PopoverCloseButton />
              <PopoverBody borderRadius="14px" boxShadow="rgba(0, 0, 0, 0.3) 0px 0px 10px 0px">
                <Text>{t('Customer Support Consent.my energy data is shared')}</Text>
              </PopoverBody>
            </PopoverContent>
          </Popover>
        </SettingSwitch>
      </Skeleton>

      <AlertDialogModal
        isOpen={isAlertOpen}
        onClose={onCloseAlert}
        header={t('Common.are you sure?')}
        subHeader={t('Customer Support Consent.customer support will have no access')}
        confirmButtonName={t('Common.yes')}
        cancelButtonName={t('Common.no')}
        onConfirm={() => handleCustomerSupportConsentToggle(false)}
      />
    </>
  );
}
